import React, { useState } from 'react';
import {
    Box,
    Typography,
    useMediaQuery,
    Paper,
    Button,
} from '@mui/material';
import { useLocation } from 'react-router-dom';

import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useDashboard } from '../../contexts/DashboardContext';
import { useDashboardData } from '../../hooks/useDashboardData';
import theme from '../../theme/theme';
import { detectLanguageFromPathname, stripLanguagePrefix, translate } from '../../i18n';
import { isAiPromptSupported } from '../../utils/decisionSummaryGenerator';
import AIPromptModal from '../dashboard/AIPromptModal';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';

function AdviceSection() {
    const flags = useFeatureFlags();
    if (!flags.aiCorner) {
        return null;
    }

    const {
        location: selectedLocation,
        action: selectedAction,
        year: selectedYear,
        month: selectedMonth,
    } = useDashboard();

    const {
        loading,
        data,
        calendarData,
    } = useDashboardData();
    const location = useLocation();
    const language = detectLanguageFromPathname(location.pathname);
    const t = (key, params) => translate(language, key, params);
    const isMobile = useMediaQuery('(max-width:768px)');
    const [aiPromptOpen, setAiPromptOpen] = useState(false);

    const normalizedPath = stripLanguagePrefix(location.pathname);
    const isPurposePage = normalizedPath === '/' || normalizedPath === '/purpose';
    const isFunctionPage = normalizedPath === '/function';

    const getThemeColors = () => {
        if (isPurposePage) {
            return {
                main: '#4A5568',
                light: '#718096',
                dark: '#2D3748',
                contrastText: '#FFFFFF',
            };
        }
        if (isFunctionPage) {
            return {
                main: '#553C9A',
                light: '#805AD5',
                dark: '#44337A',
                contrastText: '#FFFFFF',
            };
        }
        return theme.palette.primary;
    };

    const themeColors = getThemeColors();

    const shouldShowAdviceSection = () => {
        if (loading) {
            return false;
        }

        const needsLocation = selectedAction !== 'foreigners_distribution'
            && selectedAction !== 'foreigners_yearly_distribution';
        if (!selectedAction || !selectedYear || !selectedMonth || (needsLocation && !selectedLocation)) {
            return false;
        }

        if (!data && (!calendarData || calendarData.length === 0)) {
            return false;
        }

        return true;
    };

    const hasPromptData =
        (calendarData && calendarData.length > 0)
        || (Array.isArray(data) && data.length > 0);

    const showAiPromptEntry =
        shouldShowAdviceSection()
        && isAiPromptSupported(selectedAction)
        && hasPromptData;

    if (!shouldShowAdviceSection()) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            elevation={0}
            sx={{
                borderRadius: 2,
                mb: 3,
                borderColor: 'divider',
                bgcolor: 'background.paper',
            }}
        >
            <Box sx={{
                p: isMobile ? 1.5 : 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
            }}
            >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
                    <SmartToyOutlinedIcon
                        sx={{
                            fontSize: isMobile ? '1.35rem' : '1.5rem',
                            color: 'text.secondary',
                            mt: 0.15,
                        }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, lineHeight: 1.35 }}>
                            {t('aiPrompt.sectionTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                            {t('aiPrompt.sectionLead')}
                        </Typography>
                    </Box>
                </Box>

                {showAiPromptEntry ? (
                    <Button
                        variant="contained"
                        fullWidth
                        size="medium"
                        startIcon={<SmartToyOutlinedIcon />}
                        onClick={() => setAiPromptOpen(true)}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            py: 1,
                            backgroundColor: themeColors.main,
                            '&:hover': {
                                backgroundColor: themeColors.dark,
                            },
                        }}
                    >
                        {t('aiPrompt.openButton')}
                    </Button>
                ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                        {t('aiPrompt.unsupported')}
                    </Typography>
                )}
            </Box>

            <AIPromptModal open={aiPromptOpen} onClose={() => setAiPromptOpen(false)} />
        </Paper>
    );
}

export default AdviceSection;
